import React from "react";

import { faExclamationTriangle } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { Participant } from "~/calendar/hooks/useParticipantsOptions";
import { AppContext } from "~/common";

import styles from "./MeetingGuidelines.module.css";

interface MeetingAttendeesProps {
	attendees: Partial<Participant>[];
	maxRecommendedAttendees: number;
	setRendered: (rendered: boolean) => void;
}

export default function MeetingAttendees({ attendees, maxRecommendedAttendees, setRendered }: MeetingAttendeesProps) {
	const { user } = React.useContext(AppContext);

	let requiredAttendees = attendees.filter(attendee => !attendee?.isOptional);
	const userInAttendees = requiredAttendees.find(attendee => attendee.id === user?.id);
	if (!userInAttendees) {
		// Add empty entry to the array to account for the organizer
		requiredAttendees = [{}, ...requiredAttendees];
	}

	const shouldRenderPrompt = requiredAttendees.length > maxRecommendedAttendees;

	React.useEffect(() => {
		setRendered(shouldRenderPrompt);

		return () => {
			setRendered(false);
		};
	}, [setRendered, shouldRenderPrompt]);

	if (!shouldRenderPrompt) return null;

	const numAttendeesToRemove = requiredAttendees.length - maxRecommendedAttendees;

	return (
		<div className={styles.meetingPrompt}>
			<div className={styles.meetingPromptIcon}>
				<FontAwesomeIcon fixedWidth icon={faExclamationTriangle} />
			</div>
			<div>
				<div className={styles.headlineText}>
					Remove {numAttendeesToRemove} attendee
					{numAttendeesToRemove > 1 ? "s" : ""} or mark them as optional
				</div>
				<div className={styles.supportingText}>
					Meetings are most effective with fewer than {maxRecommendedAttendees + 1} attendees.
				</div>
			</div>
		</div>
	);
}
